import React, { useState, useEffect } from 'react';
import {
  Sparkles, Send, ArrowRight, Briefcase, Target, Bell, Gauge,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../config';
import { useAuth } from '../context/AuthContext';
import { useBilling } from '../context/BillingContext';
import OnboardingChecklist from './OnboardingChecklist';

// Welcome experience: a broad rectangular Ask Caply search box (70% of
// the row) with the onboarding checklist beside it (30%). The checklist
// column disappears once everything is done, and the search box then
// expands to the full width. Opens the global AI drawer with the
// question pre-sent.

function Tile({ icon: Icon, label, value, hint, onClick, accent }) {
  return (
    <button
      onClick={onClick}
      className="card !p-4 text-left hover:border-brand-300 hover:shadow-cardHover transition flex items-center gap-3"
    >
      <div className={`p-2.5 rounded-lg shrink-0 ${accent}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-slate-400">{label}</p>
        <p className="text-lg font-bold text-navy-900 leading-tight">{value}</p>
        {hint && <p className="text-[11px] text-slate-400 truncate">{hint}</p>}
      </div>
    </button>
  );
}

export default function WelcomeHero() {
  const { user } = useAuth();
  const { billing } = useBilling();
  // The public demo answers nothing through the assistant, so it does not
  // offer the box at all.
  const inDemoWorkspace = billing?.plan_id === 'demo';
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [stats, setStats] = useState({ deals: null, targets: null, alerts: null });
  const [onboarding, setOnboarding] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get('/api/intake/deals').catch(() => null),
      api.get('/api/sourcing/targets').catch(() => null),
      api.get('/api/monitoring/alerts?active_only=true').catch(() => null),
      api.get('/api/firm/onboarding').catch(() => null),
    ]).then(([deals, targets, alerts, onb]) => {
      if (!alive) return;
      setStats({
        deals: deals ? (deals.data.length ?? deals.data?.deals?.length ?? 0) : null,
        targets: targets ? (targets.data.targets?.length ?? 0) : null,
        alerts: alerts ? (Array.isArray(alerts.data) ? alerts.data.length : alerts.data.alerts?.length ?? 0) : null,
      });
      if (onb) setOnboarding(onb.data);
    });
    return () => { alive = false; };
  }, []);

  const firstName = (user?.name || 'there').split(' ')[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  // The checklist sticks around until EVERY step is done (it can be
  // collapsed but never dismissed); only then does the column go away and
  // the search box takes the whole row.
  const showChecklist = !!(onboarding && !onboarding.ready);

  const askCaply = (e) => {
    e.preventDefault();
    const q = question.trim();
    // The Layout-level drawer listens for this event and opens + sends.
    window.dispatchEvent(new CustomEvent('caply:ask', { detail: { question: q } }));
    setQuestion('');
  };

  return (
    <div className="space-y-5">
      {/* Compact search widget + flexible onboarding checklist beside it */}
      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <div className={`card relative overflow-hidden !p-4 lg:min-w-0 ${inDemoWorkspace ? 'lg:w-full' : 'lg:w-[60%]'}`}>
          <div className="relative">
            <h2 className="text-base font-bold text-navy-900">{greeting}, {firstName}</h2>
            {!inDemoWorkspace && (
            <>
            <form onSubmit={askCaply} className="mt-2.5">
              {/* 4in x 1.5in search box; the Ask button sits inside the field */}
              <div className="relative w-full">
                <Sparkles className="w-4 h-4 text-gold-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask Caply anything about your deals or portfolio"
                  className="w-full h-[72px] border border-slate-200 rounded-lg pl-10 pr-24 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-300"
                />
                <button
                  type="submit"
                  disabled={!question.trim()}
                  className="btn-primary text-sm px-4 py-2 rounded-lg disabled:opacity-40 absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" /> Ask
                </button>
              </div>
            </form>
            <div className="mt-2 flex flex-wrap gap-1.5 w-full">
              {['What deals are in my pipeline?', 'Summarize my portfolio performance', 'Any alerts I should know about?'].map((s) => (
                <button
                  key={s}
                  onClick={() => window.dispatchEvent(new CustomEvent('caply:ask', { detail: { question: s } }))}
                  className="text-[11px] text-slate-500 hover:text-brand-700 border border-slate-200 hover:border-brand-300 rounded-full px-2.5 py-1 transition"
                >
                  {s}
                </button>
              ))}
            </div>
            </>
            )}
          </div>
        </div>

        {showChecklist && (
          <div className={`lg:min-w-0 ${inDemoWorkspace ? 'lg:w-full' : 'lg:w-[40%]'}`}>
            <OnboardingChecklist state={onboarding} compact />
          </div>
        )}
      </div>

      {/* Quick tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Tile
          icon={Briefcase}
          label="Deals in pipeline"
          value={stats.deals ?? '—'}
          hint={stats.deals > 0 ? 'Open Active Deals' : 'Add your first deal'}
          onClick={() => navigate('/reports')}
          accent="bg-brand-600"
        />
        <Tile
          icon={Target}
          label="Sourcing targets"
          value={stats.targets ?? '—'}
          hint={stats.targets > 0 ? 'Find Deals' : 'Build your universe'}
          onClick={() => navigate('/sourcing')}
          accent="bg-gold-600"
        />
        <Tile
          icon={Bell}
          label="Active alerts"
          value={stats.alerts ?? '—'}
          hint={stats.alerts > 0 ? 'Needs attention' : 'All clear'}
          onClick={() => navigate('/monitoring')}
          accent={stats.alerts > 0 ? 'bg-rose-500' : 'bg-emerald-600'}
        />
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Gauge className="w-3.5 h-3.5" />
        Portfolio performance below
        <ArrowRight className="w-3 h-3" />
      </div>
    </div>
  );
}
